import { useEffect, useRef, useState, type FC } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Tag, Typography, Space } from 'antd';
import {
  ArrowRightOutlined,
  CheckCircleOutlined,
  DownOutlined,
} from '@ant-design/icons';
import type { Project } from '@/types';
import { trackProjectClick } from '@/utils/analytics';

const { Title, Paragraph, Text } = Typography;

interface ProjectCardProps {
  project: Project;
}

export const ProjectCard: FC<ProjectCardProps> = ({ project }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [fadeTop, setFadeTop] = useState(false);
  const [fadeBottom, setFadeBottom] = useState(false);
  // Skeleton shows until the cover image fires onLoad (matches the
  // AboutPhoto shimmer treatment).
  const [coverLoaded, setCoverLoaded] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const update = () => {
      const overflows = el.scrollHeight > el.clientHeight + 1;
      const atTop = el.scrollTop <= 0;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 4;
      setFadeTop(overflows && !atTop);
      setFadeBottom(overflows && !atBottom);
    };

    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, []);

  // Warm up the ProjectDetail lazy chunk so clicking a card never hits the
  // Suspense fallback (PageLoader spinner). That fallback breaks the
  // view-transition morph from Home, where the chunk hasn't been loaded yet.
  // The browser caches the module after one fetch, so this is a no-op on
  // subsequent calls and across cards.
  useEffect(() => {
    void import('@/pages/ProjectDetail');
  }, []);

  const handleClick = () => {
    // Save the exact scroll position so Home/Projects can restore it when
    // the user comes back — they end up right where they left off.
    sessionStorage.setItem('projects-restore-scroll', String(window.scrollY));
    trackProjectClick(project.title);
  };

  const content = (
    <div className="project-card hoverable-card">
      {/* Cover image (static, top). The view-transition-name is shared with
          the hero on the detail page so the browser morphs this image into
          the full-bleed hero on click — iOS app-icon-to-app effect. */}
      <div
        className="project-card__cover"
        style={{ viewTransitionName: `project-${project.id}` }}
      >
        {project.image ? (
          <>
            {!coverLoaded && (
              <div className="image-skeleton" aria-hidden="true" />
            )}
            <img
              src={project.image}
              alt={project.title}
              loading="lazy"
              decoding="async"
              onLoad={() => setCoverLoaded(true)}
              className={`image-fade${coverLoaded ? ' is-loaded' : ''}`}
              style={{
                width: '100%',
                height: 180,
                objectFit: 'cover',
                display: 'block',
              }}
            />
          </>
        ) : (
          <div
            className="project-card__pattern"
            style={{
              width: '100%',
              height: 180,
            }}
          />
        )}
      </div>

      {/* Scrollable middle section with fade mask */}
      <div
        className={`project-card__scroll${fadeTop ? ' is-fade-top' : ''}${fadeBottom ? ' is-fade-bottom' : ''}`}
        ref={scrollRef}
      >
        <div className="project-card__header">
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
            <Tag style={{ margin: 0 }}>{project.category}</Tag>
            {project.industry && (
              <Tag style={{ margin: 0 }}>{project.industry}</Tag>
            )}
          </div>
          <Title
            level={3}
            style={{ marginBottom: 0, letterSpacing: '-0.01em', fontSize: 20 }}
          >
            {project.title}
          </Title>
        </div>

        <div className="project-card__description">
          <Paragraph
            style={{ fontSize: 14, lineHeight: 1.7, marginBottom: 0, color: 'var(--color-text)' }}
          >
            {project.description}
          </Paragraph>
        </div>

        <div className="project-card__achievements">
          <Text
            strong
            style={{
              fontSize: 12,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              opacity: 0.5,
            }}
          >
            Key Achievements
          </Text>
          <ul style={{ paddingLeft: 0, listStyle: 'none', marginTop: 8, marginBottom: 0 }}>
            {project.achievements.map((achievement, i) => (
              <li
                key={i}
                style={{
                  display: 'flex',
                  gap: 8,
                  marginBottom: 6,
                  fontSize: 13,
                  lineHeight: 1.6,
                }}
              >
                <CheckCircleOutlined
                  style={{
                    color: 'var(--color-accent)',
                    marginTop: 4,
                    flexShrink: 0,
                  }}
                />
                <span>{achievement}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="project-card__tech">
          <Space size={[6, 6]} wrap>
            {project.techStack.map((tech) => (
              <Tag key={tech} style={{ fontSize: 12 }}>
                {tech}
              </Tag>
            ))}
          </Space>
        </div>
      </div>

      {/* Scroll hint — static down arrow, shown only while there's more to scroll */}
      <div
        className={`project-card__scroll-hint${fadeBottom ? ' is-visible' : ''}`}
        aria-hidden="true"
      >
        <DownOutlined />
      </div>

      {/* Static footer */}
      <div className="project-card__footer">
        <Text
          style={{
            fontSize: 14,
            fontWeight: 500,
            color: 'var(--color-accent)',
          }}
        >
          View Project <ArrowRightOutlined />
        </Text>
      </div>
    </div>
  );

  // Skip the iOS-style morph view-transition on mobile — on a small screen
  // it costs noticeably more than it gains (the source card is most of the
  // viewport, the transition flickers, and frame budget is tight). Desktop
  // keeps the animation. Detection runs at render — fine since users very
  // rarely cross the breakpoint mid-session.
  const isMobile =
    typeof window !== 'undefined' &&
    window.matchMedia('(max-width: 768px)').matches;

  return (
    <Link
      to={`/portfolio/${project.id}`}
      {...(isMobile ? {} : { viewTransition: true })}
      onClick={handleClick}
      className="project-card__link"
      data-project-id={project.id}
      state={{ from: location.pathname + location.search }}
    >
      {content}
    </Link>
  );
};
